import { useState, useEffect } from "react";

/**
 * Custom hook for persisting language settings to localStorage
 * @param {string} storageKey - localStorage key to use
 * @param {string} defaultValue - Default value if nothing is stored
 * @returns {[string, Function]} - [language, setLanguage]
 */
export const useLanguagePersistence = (storageKey, defaultValue) => {
  // Initialize state from localStorage or use default
  const [language, setLanguageState] = useState(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        return stored;
      }
    } catch (error) {
      console.warn(`Failed to read ${storageKey} from localStorage:`, error);
    }
    return defaultValue;
  });

  // Save to localStorage whenever language changes
  useEffect(() => {
    try {
      if (language) {
        localStorage.setItem(storageKey, language);
      }
    } catch (error) {
      console.warn(`Failed to save ${storageKey} to localStorage:`, error);
    }
  }, [language, storageKey]);

  // Wrapper function to update language
  const setLanguage = (newLanguage) => {
    setLanguageState(newLanguage);
  };

  return [language, setLanguage];
};

/**
 * Hook specifically for UI language (en, he, ar, es)
 */
export const useUILanguage = () => {
  return useLanguagePersistence("medilis_ui_language", "en");
};

/**
 * Hook specifically for dictation language (en-US, es-ES, he-IL, etc.)
 */
export const useDictationLanguage = () => {
  return useLanguagePersistence("medilis_dictation_language", "en-US");
};
